import captureException from 'lib/captureException';
import TrackingDataContext from 'contexts/TrackingDataContext';
import Layout from 'components/Layout';
import Dealer from 'components/Dealer';
import FetchDealerPageUseCase from 'lib/UseCases/FetchDealerPageUseCase';
import ApiDealerPageRepository from 'lib/Repositories/ApiDealerPageRepository';
import DealerPageIndexPresenter from 'lib/Presenters/DealerPageIndexPresenter';
import Error from 'pages/_error';
import StructuredDataComponent from 'components/StructuredData';
import AutoDealerStructuredData from 'lib/StructuredData/DealerPage/AutoDealer';
import { DEALER_PAGE_URL } from 'lib/ContentGroups';
import useAdSetup from 'lib/hooks/useAdSetup';
import { DealerPageViewModel } from 'lib/Presenters/DealerPageIndexPresenter/types';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import { GetServerSideProps } from 'next';
import { omitBy, isUndefined } from 'lodash';
import { SourcePages } from 'lib/EventSchema';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import { AgeType, PageType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import { enableContentCaching } from 'lib/cacheControl';
import isIndexable from 'lib/isIndexable';

interface Props {
  dealerPage?: DealerPageViewModel;
  path?: string;
  errorStatusCode?: number;
  dealer?: Record<string, unknown>;
  reviews?: [];
  indexable: boolean;
}

function DealerPage({ dealerPage, errorStatusCode, indexable }: Props): JSX.Element {
  const carGurusAdUnit = new CarGurusAdUnit({ zone: Zone.Research });

  useContentGroups({ default: DEALER_PAGE_URL });

  useAdSetup(carGurusAdUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.Dealers,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  if (!dealerPage || errorStatusCode) {
    return <Error statusCode={errorStatusCode} />;
  }

  const { titleTag, metadescription, url, enabled } = dealerPage;

  return (
    <TrackingDataContext.Provider value={{ source_page: url as SourcePages }}>
      <>
        <Layout title={titleTag} description={metadescription} indexed={enabled && indexable}>
          <Dealer dealerPage={dealerPage} />
        </Layout>
        <StructuredDataComponent data={new AutoDealerStructuredData(dealerPage)} />
      </>
    </TrackingDataContext.Provider>
  );
}

type QueryParams = {
  slug: string;
};

export const getServerSideProps: GetServerSideProps<Props, QueryParams> = async ({
  query,
  req,
  res,
}) => {
  const fetchDealerPage = new FetchDealerPageUseCase(ApiDealerPageRepository);
  const indexable = isIndexable(req);
  let entity;

  try {
    entity = await fetchDealerPage.execute(query.slug);
  } catch (error) {
    captureException(error);
    // When there is an api error, we don't want the page to 500 because we
    // want graceful error handling on Autolist-web, the api has its own error
    // monitoring
    const errorStatusCode = error.status || 500;
    if (res) res.statusCode = errorStatusCode;
    return { props: { errorStatusCode, indexable } };
  }

  const viewModel = DealerPageIndexPresenter.viewModelFrom(entity);

  enableContentCaching(res);

  return {
    props: {
      dealerPage: omitBy(viewModel, isUndefined) as DealerPageViewModel,
      indexable,
    },
  };
};

export default DealerPage;
